import { X } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { usePresence } from '@/hooks/usePresence'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { useStores } from '@/stores'
import { FormHelp } from '@/features/help/PageHelp'
import { Button } from './Button'

type SheetProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: SheetWidth
  /**
   * right  — side panel over the list: small reference records.
   * center — modal in the middle of the screen: large documents (proformas,
   *          orders…) that need width and the user's full attention.
   */
  placement?: 'right' | 'center'
  headerAside?: ReactNode
  /** The body fills the sheet with no padding or scroll of its own (a chat scrolls its own list). */
  bare?: boolean
  /** Shows the page's «?» with how to fill the form (create / edit sheets). */
  help?: boolean
}

export type SheetWidth = 'md' | 'lg' | 'xl' | '2xl'

const WIDTH_STEPS: SheetWidth[] = ['md', 'lg', 'xl', '2xl']
const WIDTH_SHIFT = { narrow: -1, standard: 0, wide: 1 } as const

const sideWidths: Record<SheetWidth, string> = {
  md: 'max-w-[480px]',
  lg: 'max-w-[600px]',
  xl: 'max-w-[760px]',
  '2xl': 'max-w-[920px]',
}
const centerWidths: Record<SheetWidth, string> = {
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[960px]',
  '2xl': 'max-w-[1160px]',
}

/**
 * Record editor. Linkable via the URL (?sheet=…) in either placement. The
 * drawer's side and width step follow the user's appearance settings.
 */
export const Sheet = observer(function Sheet({ open, onClose, title, description, children, footer, width = 'md', placement = 'right', headerAside, help, bare }: SheetProps) {
  const { appearance } = useStores()
  const { drawerSide, drawerWidth, outsideClose } = appearance.settings
  const center = placement === 'center'
  const left = !center && drawerSide === 'left'
  const step = WIDTH_STEPS.indexOf(width) + WIDTH_SHIFT[drawerWidth]
  const size = WIDTH_STEPS[Math.max(0, Math.min(WIDTH_STEPS.length - 1, step))]
  const t = useT()
  const ref = useRef<HTMLDivElement>(null)
  const presence = usePresence(open, 220)
  useFocusTrap(ref, open, onClose)
  if (!presence.mounted) return null

  return createPortal(
    <div className={cn('fixed inset-0 z-50 flex', center ? 'items-center justify-center p-3 sm:p-6' : left ? 'justify-start' : 'justify-end')}>
      <div
        data-state={presence.state}
        className={cn(
          'motion-overlay absolute inset-0',
          center
            ? 'bg-[rgba(8,14,16,0.3)] backdrop-blur-[6px] dark:bg-[rgba(0,0,0,0.55)]'
            : 'bg-[rgba(8,14,16,0.18)] backdrop-blur-[2px] dark:bg-[rgba(0,0,0,0.45)]',
        )}
        onClick={outsideClose ? onClose : undefined}
        // Keep focus inside so Esc still closes it when outside clicks don't.
        onMouseDown={(event) => event.preventDefault()}
        aria-hidden
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        tabIndex={-1}
        data-state={presence.state}
        className={cn(
          'glass-strong relative flex w-full flex-col overflow-hidden rounded-[var(--radius-shell)] outline-none',
          center
            ? cn('motion-dialog max-h-[calc(100dvh-24px)] sm:max-h-[calc(100dvh-48px)]', centerWidths[size])
            : cn(left ? 'motion-drawer' : 'motion-sheet', 'm-2 sm:m-3', sideWidths[size]),
        )}
      >
        <header className={cn('flex shrink-0 items-start justify-between gap-4 border-b border-line pb-4 pt-5', center ? 'px-7' : 'px-6')}>
          <div className="min-w-0">
            <h2 id="sheet-title" className="truncate text-[length:calc(17px*var(--text-scale))] font-semibold tracking-[-0.01em] text-fg">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-[length:calc(13px*var(--text-scale))] text-muted">{description}</p>}
          </div>
          <div className="flex items-center gap-1">
            {headerAside}
            {help && <FormHelp />}
            <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('workbench.closePanel')}>
              <X className="size-4" />
            </Button>
          </div>
        </header>
        {bare ? <div className="flex min-h-0 flex-1 flex-col">{children}</div> : <div className={cn('min-h-0 flex-1 overflow-y-auto py-5', center ? 'px-7' : 'px-6')}>{children}</div>}
        {footer && (
          <footer className={cn('flex shrink-0 items-center justify-end gap-2 border-t border-line py-3.5', center ? 'px-7' : 'px-6')}>
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
})
